import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DownloadSimple, Crown, ArrowLeft, Lock } from "@phosphor-icons/react";

const typeLabel = { icon_pack: "Icon Pack", widget: "Widget", wallpaper: "Wallpaper" };

export default function ItemDetail() {
  const { id } = useParams();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get(`/items/${id}`).then((r) => setItem(r.data)).catch(() => setItem(false));
  }, [id]);

  const handleDownload = async () => {
    if (!user) return login();
    if (!user.is_premium && !user.is_admin) {
      toast.error("Premium required", { description: "Subscribe to download unlimited packs." });
      return navigate("/premium");
    }
    setDownloading(true);
    try {
      const res = await api.post(`/items/${id}/download`);
      toast.success("Download unlocked!", { description: `${res.data.title} is yours.` });
      setItem((p) => ({ ...p, download_count: p.download_count + 1 }));
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error("Premium required");
        navigate("/premium");
      } else {
        toast.error("Something went wrong");
      }
    } finally {
      setDownloading(false);
    }
  };

  if (item === false) return <div className="max-w-[1400px] mx-auto px-5 py-32 text-center text-white/50">Item not found.</div>;
  if (!item) return <div className="max-w-[1400px] mx-auto px-5 py-32 text-center text-white/50">Loading…</div>;

  const canDownload = user && (user.is_premium || user.is_admin);

  return (
    <div className="grid-bg min-h-screen">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-12">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white/50 hover:text-white transition-colors mb-8">
          <ArrowLeft size={18} /> Back
        </button>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Phone mockup preview */}
          <div className="relative flex justify-center">
            <div className="absolute inset-0 bg-[#00E5FF]/10 blur-3xl rounded-full" />
            <div className="relative w-[300px] rounded-[2.5rem] border-[6px] border-[#1a1a2e] bg-[#0A0A14] p-2 shadow-[0_0_40px_rgba(0,229,255,0.15)]">
              <div className="rounded-[2rem] overflow-hidden aspect-[9/19]">
                <img src={item.preview_image} alt={item.title} className="w-full h-full object-cover" />
              </div>
              <div className="absolute top-4 left-1/2 -translate-x-1/2 w-24 h-5 bg-[#1a1a2e] rounded-full" />
            </div>
          </div>

          {/* Details */}
          <div>
            <span className="inline-block text-[10px] font-bold uppercase tracking-[0.15em] px-3 py-1 rounded-sm bg-[#00E5FF] text-[#05050A]">
              {typeLabel[item.type]}
            </span>
            <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter uppercase mt-5 leading-none">
              {item.title}
            </h1>
            <div className="flex items-center gap-4 mt-4 text-white/50 text-sm">
              <span>{item.category}</span>
              <span className="flex items-center gap-1"><DownloadSimple size={15} /> {item.download_count} downloads</span>
            </div>
            <p className="text-white/70 text-lg leading-relaxed mt-8">{item.description}</p>

            <div className="glass rounded-lg p-6 mt-10">
              <div className="flex items-center gap-2 text-[#FFEB3B] mb-2">
                <Crown weight="fill" size={20} />
                <span className="font-bold uppercase tracking-wider text-sm">Premium Content</span>
              </div>
              <p className="text-white/50 text-sm mb-5">
                {canDownload
                  ? (user.is_premium ? "You're premium. Download away, you beautiful genius." : "Admin access. Download anything, boss.")
                  : "Unlock this and every other pack with a premium subscription."}
              </p>
              <button
                data-testid="download-item-btn"
                onClick={handleDownload}
                disabled={downloading}
                className={`w-full flex items-center justify-center gap-2 font-bold py-4 rounded-sm transition-all disabled:opacity-60 ${
                  canDownload
                    ? "bg-[#FFEB3B] text-[#05050A] hover:bg-[#fff35c] hover:shadow-[0_0_28px_rgba(255,235,59,0.5)]"
                    : "bg-white/10 text-white hover:bg-white/15 border border-white/20"
                }`}
              >
                {canDownload ? (
                  <><DownloadSimple weight="bold" size={20} /> {downloading ? "Preparing…" : "Download now"}</>
                ) : (
                  <><Lock weight="fill" size={18} /> Unlock with Premium</>
                )}
              </button>
              {!user && (
                <p className="text-white/40 text-xs text-center mt-4">
                  <button onClick={login} className="text-[#00E5FF] hover:underline">Sign in</button> to download
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
